import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import CustomModal from '../customModal';
import VirtualizedList from '../virtualizedList';
import CourseCard from '../courseCard';
import Search from '../search';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  coursesWrapper: {
    minHeight: 196,
    maxHeight: 450,
    width: '100%',
  },
  coursesViewport: {
    minHeight: 196,
    maxHeight: 450,
  },
  course: {
    width: '100%',
  },
  enrolledCourse: {
    cursor: 'default',
    '&:hover': {
      border: `1px solid ${primary.bluish6}`,
    },
  },
  selectedCourse: {
    border: `1px solid ${primary.blue1}`,
  },
  filteringAndSearch: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing(4),
    width: 469,
  },
  search: {
    marginLeft: 'auto',
    width: 180,
  },
  scrollY: {
    right: -19,
  },
}));

const EnrollCourseDialog = ({
  translations,
  isOpened,
  courses,
  levels,
  onCancel,
  onEnroll,
  onSearch,
  getCourseEnrolledUsers,
}) => {
  const classes = useStyles();

  const [selectedCourse, setSelectedCourse] = useState(null);

  const handleSetSelectCourse = course => setSelectedCourse(course || null);

  const onSelectCourse = course => () => {
    if (course.enrollmentStatus) return;

    return handleSetSelectCourse(course);
  };

  const handleClose = () => {
    handleSetSelectCourse();
    onCancel();
  };

  const handleConfirm = () => {
    onEnroll(selectedCourse).then(() => {
      handleSetSelectCourse();
    });
  };

  const handleGetEnrolledUsers = courseId => () =>
    getCourseEnrolledUsers(courseId);

  const getVirtualCourseSize = useCallback(() => 180, []);

  const renderCourse = course => (
    <CourseCard
      className={classNames(classes.course, {
        [classes.selectedCourse]: selectedCourse?.id === course.id,
        [classes.enrolledCourse]: course.enrollmentStatus,
      })}
      translations={translations.courseLabels}
      course={course}
      levels={levels}
      shouldDisablePortal={false}
      getCourseEnrolledUsers={handleGetEnrolledUsers(course.id)}
      onCardClick={onSelectCourse(course)}
      hasEnrolledUsersCount
      hasCourseUrl
    />
  );

  return (
    <CustomModal
      title={translations.title}
      isOpened={isOpened}
      closeButtonLabel={translations.cancel}
      confirmButtonLabel={translations.create}
      isConfirmDisabled={selectedCourse === null}
      onClose={handleClose}
      onConfirm={handleConfirm}
      isMedium
    >
      <div>
        <div>
          <div className={classes.filteringAndSearch}>
            <Search
              className={classes.search}
              placeholder={translations.search}
              onChange={onSearch}
            />
          </div>
        </div>
        <div className={classes.coursesWrapper}>
          <VirtualizedList
            viewportClass={classes.coursesViewport}
            verticalScrollClass={classes.scrollY}
            itemClass={classes.userWrapper}
            items={courses}
            count={courses?.length ?? 0}
            gap={16}
            estimateSize={getVirtualCourseSize}
            renderItem={renderCourse}
            isDynamicSize
          />
        </div>
      </div>
    </CustomModal>
  );
};

EnrollCourseDialog.propTypes = {
  translations: PropTypes.object.isRequired,
  isOpened: PropTypes.bool.isRequired,
  courses: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  levels: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getCourseEnrolledUsers: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onEnroll: PropTypes.func.isRequired,
};

export default EnrollCourseDialog;
